// ============================================================
// The Shape tool's structures - pillars, cones and domes, the shapes
// cubes do badly - and the one selected among them.
// ============================================================
import { useState, useCallback, useMemo } from 'react';
import { makeStructure, addStructure, updateStructure, removeStructure } from '../engine/battlefield/structures';

/**
 * Place, change and remove structures. `spec` is the Shape tool's next
 * structure (shape, radius, height, rotation, material) without a place.
 *
 * Placing takes the world point, not the cell: a pillar is not on a grid,
 * and snapping one to a cell corner would be the voxel model leaking into
 * the thing that exists precisely because voxels cannot do it.
 */
export function useStructureEdits({ terrain, setTerrain, spec }) {
  const [selected, setSelected] = useState(null);
  const placeStructure = useCallback(
    (at) => {
      const solid = makeStructure({ ...spec, x: at.x, z: at.z });
      setTerrain({ ...terrain, solids: addStructure(terrain.solids || [], solid) });
      setSelected(solid.id);
      return solid;
    },
    [spec, terrain, setTerrain],
  );
  const { patchStructure, deleteStructure } = useSelectedEdits({ terrain, setTerrain, selected, setSelected });
  /** The selected structure itself, for the controls that describe it. */
  const selectedStructure = useMemo(
    () => (selected ? (terrain.solids || []).find((s) => s.id === selected) : null) || null,
    [selected, terrain],
  );
  return { selected, setSelected, placeStructure, patchStructure, deleteStructure, selectedStructure };
}

/** Change or take away the selected structure. A miss writes nothing. */
function useSelectedEdits({ terrain, setTerrain, selected, setSelected }) {
  const writeSolids = useCallback(
    (next) => {
      if (next !== terrain.solids) setTerrain({ ...terrain, solids: next });
    },
    [terrain, setTerrain],
  );
  const patchStructure = useCallback(
    (patch) => {
      if (selected) writeSolids(updateStructure(terrain.solids || [], selected, patch));
    },
    [selected, terrain.solids, writeSolids],
  );
  const deleteStructure = useCallback(() => {
    if (!selected) return;
    writeSolids(removeStructure(terrain.solids || [], selected));
    setSelected(null);
  }, [selected, terrain.solids, writeSolids, setSelected]);
  return { patchStructure, deleteStructure };
}
